import { useEffect } from "react";
import { Title, Button, Drawer, Alert, Text, Accordion, Group, TextInput, Grid } from "@mantine/core"
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconArrowBackUp, IconCheck, IconCircle, IconCircleArrowRightFilled, IconCreditCard, IconExclamationCircle, IconPencil } from "@tabler/icons-react"
import moment from "moment";

import { useProgramRegistrationState } from "../../state/programregistration.state";
import { getProgramRegistrationTotal } from "../../functions/programregistration.functions";
import { useProgramActivitiesQuery, useProgramActivitySessionsQuery, useProgramsQuery } from "../../api/program.api";
import { useUserPermissionQuery } from "../../api/user.api";
import { currency } from "../../shared/helpers";

interface PaymentRefundEditorProps {
	// paymentMethod: TransactionPaymentMethod|null;
	onRefunded?: () => void;
}

interface FormFields {
	RefundAmount: number;
	RefundReason: string;
}

const PaymentRefundEditor = ({ onRefunded }: PaymentRefundEditorProps) => {
	const [opened, { open, close }] = useDisclosure(false);
	const { data: userPermissions } = useUserPermissionQuery();

	const { programRegistration, updateProgramRegistration, isLoading: isLoadingRegistration, isDeleted, status } = useProgramRegistrationState();
	const { data: programs, isLoading: isLoadingPrograms } = useProgramsQuery();
	// const { data: programGroups, isLoading: isLoadingProgramGroups } = useProgramGroupsQuery();
	const { data: programActivities } = useProgramActivitiesQuery();
	const { data: programActivitySessions } = useProgramActivitySessionsQuery();

	const canRefund = userPermissions?.RefundPermission === true && !isLoadingRegistration &&
		programRegistration.DateRefunded === null && !isDeleted && moment(programRegistration.DateSubmitted).year() == moment().year();

	const total = getProgramRegistrationTotal(programRegistration, programs ?? [], programActivities ?? [], programActivitySessions ?? [], true);

	const form = useForm<FormFields>({
		initialValues: {
			RefundAmount: 0,
			RefundReason: ''
		},
		validate: {
			RefundAmount: (value) => value <= 0 || value > total ? 'Refund amount must be greater than 0 and cannot exceed the total' : null,
			RefundReason: (value) => value.length < 5 || value.length > 300 ? 'Refund reason must be at least 5 characters and no more than 300 characters' : null
		}
	});

	useEffect(() => {
		if (opened) {
			form.setFieldValue('RefundAmount', 0);
			form.setFieldValue('RefundReason', '');
		}
	}, [opened]);

	const handleSubmit = (values: FormFields) => {
		if (programRegistration) {
			const updatedRegistration = {
				...programRegistration,
				Refunded: values.RefundAmount,
				DateRefunded: new Date().toISOString(),
				RefundReason: values.RefundReason
			};
			updateProgramRegistration(updatedRegistration);
			close();
		}
	}


	return <>
		{userPermissions?.RefundPermission === true && !isLoadingRegistration && 
		<Button onClick={open} disabled={!canRefund} hidden={isDeleted} variant="light" size="xs" mt={-15} mb="md" leftSection={<IconArrowBackUp />}>
			Refund
		</Button>
		}

		<Drawer opened={opened} onClose={close} title={<div><Title order={3}>Refund Customer</Title></div>} position="right" withOverlay={true} size="md" withCloseButton>
			<Alert variant="light" color="indigo" icon={<IconExclamationCircle />}>
				If you need to provide a partial or full refund to the customer, please record the details below. This will cause the refunded amount to be included on the Refund Report.
			</Alert>

			<Text mt="md" >The maximum amount you can refund is:</Text>
			<Text fz="xl" fw="bold">{currency.format(total)}</Text>

			<form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
				<Grid>
					<Grid.Col span={4}>
						<TextInput
							{...form.getInputProps('RefundAmount')} 
							withAsterisk
							placeholder="Enter amount"
							label="Refund Amount"
							mt="md"
						/>
					</Grid.Col>
					<Grid.Col span={8}>
						<TextInput
							{...form.getInputProps('RefundReason')} 
							withAsterisk
							placeholder="Enter refund reason"
							label="Refund Reason"
							mt="md"
						/>
					</Grid.Col>
				</Grid>

				<Button type="submit" variant="filled" mt="md" leftSection={<IconArrowBackUp />}>
					Apply Refund to Registration
				</Button>

			</form>
			
			<Alert variant="light" color="red" icon={<IconExclamationCircle />} mt="md">
				Note: This only records the refund details on the registration. It DOES NOT process the actual refund through Stripe. You will need to request that separately.
			</Alert>
		</Drawer>
	</>
}

export default PaymentRefundEditor